import { MARKET_LEGS, MARKET_LIMITS, type MarketLegName, type MarketListingView, type MarketPaymentView, type MarketPayInstruction, type MarketPublicView, type MarketSort } from "@common/platform/market";
import { formatTokenAmount, type StoreWorn } from "@common/platform/store";
import type { Emblem } from "@common/utils/emblem";
import { refreshSavedDesigns, savedDesigns } from "../emblemEditor";
import { drawEmblem } from "../utils/emblemRenderer";
import { sentence, sentences, t, tr } from "../utils/translations/phrases";
import { sendStorePayment, storeBuyer } from "../walletFlow";

/*
 * The flea market in the store (0.98): players' own drawn emblems for sale to each other, in the game's coin. Browse
 * (the newest or the most bought), buy (two transfers from the buyer's own wallet: a tenth burned, the rest straight to
 * the seller; the server finds both in the coin's records before the emblem is theirs, worn as a picture), sell one of
 * your saved designs (the server draws it and refuses one too like a store emblem or another player's listing), and
 * your listings (price, copies, on sale or not).
 *
 * One part, one transfer, as the store's payments (storeMenu.ts): the wallet is asked to send a part only when nothing
 * this device knows of may still pay it. Each transaction the wallet sends is remembered on this device before anything
 * else and told to the market again until it has it; one the market refused is told once more before the wallet is asked
 * again; one the wallet may have sent (an error after it was asked) holds that part to checks for ten minutes, as a part
 * being paid in another tab does. A wallet switched while a purchase runs stops it.
 */

type View = "browse" | "sell" | "listings";
type MinePayment = MarketPaymentView & { readonly pay?: MarketPayInstruction };
type ListingState = "on_sale" | "withdrawn" | "removed";
interface MineListing extends MarketListingView {
    readonly state: ListingState
    readonly stock?: number
    readonly removedNote?: string
    readonly repriceNeeded?: boolean
    /** the saved design it was put up from: out of the seller's loadout while it's on sale (0.99) */
    readonly fromDesign?: string
}
interface Mine {
    readonly listings: readonly MineListing[]
    readonly bought: ReadonlyArray<MarketListingView & { readonly wearable: boolean }>
    readonly payments: readonly MinePayment[]
    /** the listings of the wallet's payments not paid (a card for each, off the market or past the pages read too) */
    readonly paying?: readonly MarketListingView[]
    readonly worn?: StoreWorn
    readonly banned?: boolean
    readonly walletRequired?: boolean
}
interface Answer {
    readonly payment: MinePayment
    readonly told?: Partial<Record<MarketLegName, { readonly state: string, readonly tx: string }>>
    readonly looked?: boolean
}

const EMPTY: Mine = { listings: [], bought: [], payments: [] };
let mine: Mine = EMPTY;
let mineFor: string | undefined;
let view: View = "browse";
let sort: MarketSort = "new";
let page: MarketPublicView | undefined;
/** the listings shown: the first page, and the ones "More" added */
let shown: MarketListingView[] = [];
let failed = false;
/** the reads of the page, counted: an answer to an older one (another sort) is never kept over a newer one's */
let pageReads = 0;
let moreLoading = false;
const said = new Map<string, { readonly text: string, readonly bad?: boolean }>();
let purchaseOn: { readonly id: number, readonly listingId: string } | undefined;
let purchases = 0;
let following = false;
const checking = new Set<string>();
let generation = 0;
let lastBuyer: string | undefined;
/** asked to draw the store's list again (storeMenu.ts) */
let redraw: () => void = () => undefined;
/** said to a screen reader */
let announce: (text: string) => void = () => undefined;

// ---- the Sell form, kept as typed (the list is drawn again every few seconds: never from the fields themselves)
/** the Sell form: `design` is the picked design's name (0.99: by its name, so a design coming back or going doesn't move the pick onto another) */
const sellForm = { design: "", name: "", price: "", stock: "" };
let selling = false;
let sellSaid: { readonly text: string, readonly bad?: boolean } | undefined;
/** the listing fields being typed (price, copies), by listing */
const edits = new Map<string, { price?: string, stock?: string }>();
const listingSaid = new Map<string, { readonly text: string, readonly bad?: boolean }>();
const listingBusy = new Set<string>();

const escapeHtml = (text: string): string => text.replace(/[&<>"']/gu, char => `&#${char.charCodeAt(0)};`);
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));
const short = (address: string): string => `${address.slice(0, 6)}…${address.slice(-4)}`;
/** a part's name, as a sentence has it (in the player's language) */
const legWords = (leg: MarketLegName): string => (leg === "burn" ? t("the burned part") : t("the seller's part"));
/** words of the market's own (an error, a payment's note, why it takes no payments): in the player's language when the phrasebook has them */
const serverWords = (words: string | undefined): string | undefined => (words ? tr(words) : words);

interface Run {
    readonly owner: string | undefined
    readonly generation: number
}
class Stopped extends Error {}
const startRun = (): Run => ({ owner: storeBuyer()?.address, generation });
const alive = (run: Run): boolean => run.generation === generation && run.owner !== undefined && storeBuyer()?.address === run.owner;
const stillOn = (run: Run): void => {
    if (!alive(run)) throw new Stopped();
};

// ---- what this device remembers, shared by its tabs
const readRecord = <T>(key: string): Record<string, T> => {
    try {
        const saved: unknown = JSON.parse(localStorage.getItem(key) ?? "{}");
        return saved && typeof saved === "object" && !Array.isArray(saved) ? saved as Record<string, T> : {};
    } catch {
        return {};
    }
};
const writeRecord = <T>(key: string, change: (all: Record<string, T>) => void): boolean => {
    try {
        const all = readRecord<T>(key);
        change(all);
        localStorage.setItem(key, JSON.stringify(all));
        return true;
    } catch {
        return false;
    }
};
interface Sent {
    readonly tx: string
    readonly at: number
    readonly told?: boolean
    /** the market refused it: told once more before the wallet is asked again */
    readonly dropped?: boolean
}
const SENT_KEY = "trenchio_market_sent";
const SENT_KEPT_MS = MARKET_LIMITS.latePaymentMs + 24 * 60 * 60_000;
const HASH = /^0x[0-9a-f]{64}$/u;
const sentHere = new Map<string, Sent>();
const partKey = (paymentId: string, leg: MarketLegName): string => `${paymentId}:${leg}`;
const sentOf = (paymentId: string, leg: MarketLegName): Sent | undefined => {
    const key = partKey(paymentId, leg);
    const kept = readRecord<Sent>(SENT_KEY)[key] ?? sentHere.get(key);
    return kept && typeof kept.tx === "string" && HASH.test(kept.tx) && Date.now() - kept.at < SENT_KEPT_MS ? kept : undefined;
};
const keepSent = (paymentId: string, leg: MarketLegName, entry: Sent): void => {
    const key = partKey(paymentId, leg);
    sentHere.set(key, entry);
    writeRecord<Sent>(SENT_KEY, all => {
        all[key] = entry;
        for (const [other, value] of Object.entries(all)) if (!(Date.now() - (value?.at ?? 0) < SENT_KEPT_MS)) delete all[other];
    });
};
const rememberSent = (paymentId: string, leg: MarketLegName, tx: string, told = false): void => keepSent(paymentId, leg, { tx, at: sentOf(paymentId, leg)?.at ?? Date.now(), ...(told ? { told } : {}) });
const dropSent = (paymentId: string, leg: MarketLegName): void => {
    const sent = sentOf(paymentId, leg);
    if (sent) keepSent(paymentId, leg, { ...sent, dropped: true, told: false });
};
const forgetSent = (paymentId: string, leg: MarketLegName): void => {
    const key = partKey(paymentId, leg);
    sentHere.delete(key);
    writeRecord<Sent>(SENT_KEY, all => {
        delete all[key];
    });
};
const MAYBE_KEY = "trenchio_market_maybe";
const MAYBE_SENT_MS = 10 * 60_000;
const PAYING_KEY = "trenchio_market_paying";
const PAYING_ELSEWHERE_MS = 5 * 60_000;
const maybeHere = new Map<string, number>();
const fresh = (at: unknown, ms: number): boolean => typeof at === "number" && Date.now() - at < ms && at <= Date.now() + 60_000;
const maybeSent = (paymentId: string, leg: MarketLegName): boolean => fresh(readRecord<number>(MAYBE_KEY)[partKey(paymentId, leg)] ?? maybeHere.get(partKey(paymentId, leg)), MAYBE_SENT_MS);
const markMaybeSent = (paymentId: string, leg: MarketLegName): void => {
    maybeHere.set(partKey(paymentId, leg), Date.now());
    writeRecord<number>(MAYBE_KEY, all => {
        all[partKey(paymentId, leg)] = Date.now();
        for (const [key, at] of Object.entries(all)) if (!fresh(at, MAYBE_SENT_MS)) delete all[key];
    });
};
const payingHere = new Set<string>();
const payingElsewhere = (paymentId: string): boolean => !payingHere.has(paymentId) && fresh(readRecord<number>(PAYING_KEY)[paymentId], PAYING_ELSEWHERE_MS);
const markPaying = (paymentId: string): void => {
    payingHere.add(paymentId);
    writeRecord<number>(PAYING_KEY, all => {
        all[paymentId] = Date.now();
    });
};
const clearPaying = (paymentId: string): void => {
    payingHere.delete(paymentId);
    writeRecord<number>(PAYING_KEY, all => {
        delete all[paymentId];
    });
};

let clockSkew = 0;
const marketNow = (): number => Date.now() + clockSkew;
/** a payment's first part is sent by the wallet only with this long left at least (a part paid, the rest any time in its week) */
const PAY_MARGIN_MS = 5 * 60_000;
const LOOK_SPACING_MS = 3_100;
const lookedAt = new Map<string, number>();
const noAnswerWords = (): string => t("The game server couldn't be reached: check your connection");
const api = async <T>(path: string, init?: RequestInit): Promise<T> => {
    let response: Response;
    try {
        response = await fetch(`${API_URL}${path}`, {
            credentials: "include",
            cache: "no-store",
            ...init,
            headers: { ...(init?.body ? { "Content-Type": "application/json" } : {}), ...(init?.headers ?? {}) }
        });
    } catch {
        throw new Error(noAnswerWords());
    }
    const date = Date.parse(response.headers.get("Date") ?? "");
    if (Number.isFinite(date)) clockSkew = date - Date.now();
    const body = (await response.json().catch(() => ({}))) as T & { error?: string };
    if (!response.ok) throw new Error(serverWords(body.error) ?? t("The game server answered <status>", { status: response.status }));
    return body;
};
const lookUp = async(key: string, spaced: boolean, request: () => Promise<Answer>): Promise<Answer> => {
    const wait = LOOK_SPACING_MS - (Date.now() - (lookedAt.get(key) ?? Number.NEGATIVE_INFINITY));
    if (spaced && wait > 0) await sleep(wait);
    lookedAt.set(key, Date.now());
    return request();
};
const look = (paymentId: string, spaced = false): Promise<Answer> =>
    lookUp(`${paymentId}:`, spaced, () => api<Answer>(`/market/payments/${encodeURIComponent(paymentId)}/check`, { method: "POST" }));
const tell = (paymentId: string, leg: MarketLegName, tx: string, spaced = false): Promise<Answer> =>
    lookUp(`${paymentId}:${leg}:${tx}`, spaced, () => api<Answer>(`/market/payments/${encodeURIComponent(paymentId)}/sent`, { method: "POST", body: JSON.stringify({ leg, tx }) }));

const amount = (atomic: string): string => {
    const token = page?.token;
    return token ? `${formatTokenAmount(atomic, token.decimals)} ${token.symbol}` : "";
};

/** each listing once (pages read while listings came and went can overlap) */
const once = (items: readonly MarketListingView[]): MarketListingView[] => {
    const seen = new Set<string>();
    return items.filter(item => !seen.has(item.id) && Boolean(seen.add(item.id)));
};

/** the market's listings read again: the first page (the "More" ones too, read again as far as they went) */
const loadPage = async(): Promise<void> => {
    const read = ++pageReads;
    const sorted = sort;
    try {
        const first = await api<MarketPublicView>(`/market?sort=${sorted}`);
        const more: MarketListingView[] = [];
        for (let offset = first.listings.length; offset < Math.min(first.total, shown.length); offset += MARKET_LIMITS.pageSize) {
            const next = await api<MarketPublicView>(`/market?sort=${sorted}&offset=${offset}`);
            more.push(...next.listings);
        }
        // (a newer read started meanwhile, the sort changed perhaps: its answer is the one kept)
        if (read !== pageReads || sorted !== sort) return;
        page = first;
        shown = once([...first.listings, ...more]);
        failed = false;
    } catch {
        if (read === pageReads) failed = page === undefined;
    }
};
/** the Sell form emptied (another wallet's form is never sent by the next one) */
const resetSellForm = (): void => {
    sellForm.design = "";
    sellForm.name = "";
    sellForm.price = "";
    sellForm.stock = "";
};
const loadMine = async(): Promise<void> => {
    const buyer = storeBuyer();
    if (!buyer || buyer.guest || !buyer.verified) {
        if (mineFor !== undefined) said.clear();
        mine = { ...EMPTY, walletRequired: true };
        mineFor = undefined;
        return;
    }
    if (mineFor !== buyer.address) {
        mine = EMPTY;
        mineFor = buyer.address;
        said.clear();
        edits.clear();
        listingSaid.clear();
        resetSellForm();
    }
    try {
        const read = await api<Mine>("/market/mine");
        if (storeBuyer()?.address !== buyer.address) return;
        mine = read;
    } catch {
        // (kept as it was: the next look tries again)
    }
};
/**
 * a payment kept as the market answered it last, how to pay it too: every answer says it while it may be paid, and none
 * once it may not (bought already, the game's token changed, given up; 0.98 review: an earlier answer's was kept)
 */
const keepPayment = (payment: MinePayment): void => {
    if (mineFor === undefined || mineFor !== storeBuyer()?.address) return;
    mine = { ...mine, payments: [payment, ...mine.payments.filter(other => other.id !== payment.id)] };
};
/** a payment still looked for: waiting, or given up with one part paid (its other part taken a week) */
const active = (payment: MinePayment): boolean => payment.state === "waiting" || (payment.state === "expired" && payment.legs.burn.paid !== payment.legs.seller.paid && marketNow() - payment.expiresAt < MARKET_LIMITS.latePaymentMs);
const activeFor = (listingId: string): MinePayment | undefined => mine.payments.find(payment => payment.listingId === listingId && active(payment));
const owns = (listingId: string): boolean => mine.bought.some(item => item.id === listingId);
/** one given up in its week (nothing paid): a transfer for it may still count, late; looked for again at the buyer's word */
const lateFor = (listingId: string): MinePayment | undefined =>
    mine.payments.find(payment => payment.listingId === listingId && payment.state === "expired" && !active(payment) && marketNow() - payment.expiresAt < MARKET_LIMITS.latePaymentMs);
/** a part this device sent a while ago (an hour at most) for a payment of the listing given up since: it may still count */
const STILL_ON_ITS_WAY_MS = MARKET_LIMITS.txResolveMs;
const lateSentFor = (listingId: string): { readonly payment: MinePayment, readonly leg: MarketLegName, readonly tx: string } | undefined => {
    for (const payment of mine.payments) {
        if (payment.listingId !== listingId || payment.state !== "expired" || active(payment) || !(marketNow() - payment.expiresAt < MARKET_LIMITS.latePaymentMs)) continue;
        for (const leg of MARKET_LEGS) {
            const sent = sentOf(payment.id, leg);
            if (sent && !payment.legs[leg].paid && Date.now() - sent.at < STILL_ON_ITS_WAY_MS) return { payment, leg, tx: sent.tx };
        }
    }
    return undefined;
};
const otherLeg = (leg: MarketLegName): MarketLegName => (leg === "burn" ? "seller" : "burn");
/** a part on its way: its transaction the market has, or this device sent it (or may have) */
const onItsWay = (payment: MinePayment, leg: MarketLegName): boolean => !payment.legs[leg].paid && (!!payment.legs[leg].tx || !!sentOf(payment.id, leg) || maybeSent(payment.id, leg));
/**
 * whether a part waits: its payment's time nearly up (five minutes left at most, or past it) and its other part not paid.
 * Sent then, it could go in after the payment's time with the other not in yet, and be refused as late; once a part is
 * paid, the other is taken any time in its week (0.98 review: the second part skipped the margin for a first one merely sent)
 */
const nearlyUp = (payment: MinePayment, leg: MarketLegName): boolean => payment.state === "waiting" && !payment.legs[otherLeg(leg)].paid && payment.expiresAt - marketNow() <= PAY_MARGIN_MS;
/** past its time with the other part not paid: a part refused then may have been refused as late, and count once the other is in */
const lateAlone = (payment: MinePayment, leg: MarketLegName): boolean => marketNow() > payment.expiresAt && !payment.legs[otherLeg(leg)].paid;
const waitWords = (leg: MarketLegName): string => (leg === "burn"
    ? t("The burned part is on its way: once it's in, press Pay the rest (the rest is taken for a week then).")
    : t("The seller's part is on its way: once it's in, press Pay the rest (the rest is taken for a week then)."));

const say = (listingId: string, text: string, bad = false): void => {
    const before = said.get(listingId)?.text;
    said.set(listingId, { text, bad });
    redraw();
    if (text !== before) announce(t("<item>: <text>", { item: shown.find(item => item.id === listingId)?.name ?? t("Flea market"), text }));
};

// ---- buying: each part in its turn, the burned part first
const refusedWords = (): string => t("The flea market didn't take that transaction: nothing was counted for it");
const ownedWords = (): string => t("You have this emblem already: that transfer wasn't counted for it again");
const couldNotLookWords = (): string => t("The flea market couldn't look for your payment just now: try again in a minute (don't pay again yet).");
const onItsWayWords = (): string => t("Your earlier payment may still go through: look again in a minute (don't pay again).");

/** a part told the market: kept (its transaction is the market's now), refused (dropped: told once more before paying again) */
const afterTold = (answer: Answer, leg: MarketLegName, tx: string): string | undefined => {
    const part = answer.payment.legs[leg];
    if (part.paid || part.tx?.toLowerCase() === tx) {
        rememberSent(answer.payment.id, leg, tx, true);
        return undefined;
    }
    const told = answer.told?.[leg];
    if (told?.state === "owned") {
        forgetSent(answer.payment.id, leg);
        return serverWords(answer.payment.note) ?? ownedWords();
    }
    if (told?.state === "refused") {
        dropSent(answer.payment.id, leg);
        return serverWords(answer.payment.note) ?? refusedWords();
    }
    return undefined;
};

/** the wallet asked to send one part, once: its transaction remembered before anything else, then told to the market */
const payPart = async(payment: MinePayment, leg: MarketLegName, run: Run): Promise<MinePayment> => {
    const pay = payment.pay;
    if (!pay) throw new Error(t("The flea market didn't say how to pay it: close the store and open it again"));
    // (a part with five minutes left at least, unless the other is paid: then any time in its week)
    if (nearlyUp(payment, leg)) throw new Error(onItsWay(payment, otherLeg(leg)) ? waitWords(otherLeg(leg)) : t("That payment's time is nearly up: buy it again once it has ended (nothing was paid)."));
    if (payingHere.has(payment.id)) throw new Error(t("Your wallet is asked to pay it already: answer it there first."));
    if (payingElsewhere(payment.id)) throw new Error(t("It's being paid in another tab: look there, or wait a moment."));
    stillOn(run);
    let tx: string;
    markPaying(payment.id);
    try {
        const common = { token: pay.token, chainId: pay.chainId, decimals: pay.decimals, symbol: pay.symbol };
        tx = (await sendStorePayment(
            leg === "burn" ? { ...common, method: pay.burn.method, to: pay.burn.to, amountAtomic: pay.burn.amountAtomic } : { ...common, method: "transfer", to: pay.seller.to, amountAtomic: pay.seller.amountAtomic },
            text => {
                if (alive(run)) say(payment.listingId, leg === "burn" ? t("1 of 2, the burned tenth: <step>", { step: text }) : t("2 of 2, to the seller: <step>", { step: text }));
            }
        )).toLowerCase();
    } catch (error) {
        const failure = error as { message?: string, cancelled?: boolean, notSent?: boolean };
        const words = failure.message ?? String(error);
        if (failure.cancelled || (!failure.notSent && /\b(?:reject(?:s|ed|ion)?|denied|cancel(?:s|l?ed|l?ing|lation)?)\b/iu.test(words))) throw new Error(t("You cancelled <part> in your wallet: nothing was sent for it (Pay to go on).", { part: legWords(leg) }));
        if (failure.notSent) throw new Error(words);
        markMaybeSent(payment.id, leg);
        throw new Error(t("Your wallet answered: <words> It may have sent <part> all the same: press “I paid: check” in a minute, and don't pay again yet.", { part: legWords(leg), words }));
    } finally {
        clearPaying(payment.id);
    }
    if (!HASH.test(tx)) {
        markMaybeSent(payment.id, leg);
        throw new Error(t("Your wallet's answer wasn't a transaction: press “I paid: check” in a minute, and don't pay again yet."));
    }
    rememberSent(payment.id, leg, tx);
    stillOn(run);
    say(payment.listingId, leg === "burn" ? t("The burned part is sent: telling the flea market…") : t("The seller's part is sent: telling the flea market…"));
    let answer: Answer;
    try {
        answer = await tell(payment.id, leg, tx);
    } catch (error) {
        stillOn(run);
        throw new Error(t("Your payment was sent, but the flea market couldn't be told yet (<error>): press “I paid: check” in a moment. Don't pay again.", { error: (error as Error).message }));
    }
    stillOn(run);
    keepPayment(answer.payment);
    const refused = afterTold(answer, leg, tx);
    if (refused) throw new Error(refused);
    return answer.payment;
};

/** a part this device sent that the market doesn't have: told (again). Undefined: refused again, the wallet may pay it */
const tellAgain = async(payment: MinePayment, leg: MarketLegName, run: Run): Promise<MinePayment | undefined> => {
    const sent = sentOf(payment.id, leg);
    if (!sent) return undefined;
    const answer = await tell(payment.id, leg, sent.tx, true);
    stillOn(run);
    keepPayment(answer.payment);
    const part = answer.payment.legs[leg];
    if (part.paid || part.tx) {
        rememberSent(payment.id, leg, sent.tx, true);
        return answer.payment;
    }
    if (answer.told?.[leg]?.state === "owned") {
        forgetSent(payment.id, leg);
        throw new Error(serverWords(answer.payment.note) ?? ownedWords());
    }
    if (answer.told?.[leg]?.state === "refused" && sent.dropped && !lateAlone(answer.payment, leg)) {
        // (refused twice: it failed, or isn't this part's. Forgotten: the wallet may pay it. Never past the payment's time
        // with the other part not in: refused as late, it counts once that one is in; 0.98 review)
        forgetSent(payment.id, leg);
        return undefined;
    }
    if (answer.told?.[leg]?.state === "refused") {
        dropSent(payment.id, leg);
        throw new Error(serverWords(answer.payment.note) ?? refusedWords());
    }
    if (answer.looked === false) throw new Error(couldNotLookWords());
    return answer.payment;
};

/** a payment followed until it's paid (its transactions in blocks), at most two minutes */
const follow = async(payment: MinePayment, run: Run): Promise<void> => {
    following = true;
    redraw();
    const deadline = Date.now() + 2 * 60_000;
    let current = payment;
    // (each part's transaction the market had: one it has no more, not refused and not this device's, is another device's
    // lost in a restart: its page tells it again, Pay held back meanwhile, as the store's are; 0.98 review)
    const followed: Partial<Record<MarketLegName, string>> = {};
    for (const leg of MARKET_LEGS) if (current.legs[leg].tx) followed[leg] = current.legs[leg].tx?.toLowerCase();
    while (current.state !== "paid" && active(current) && Date.now() < deadline) {
        // (a part neither paid nor on its way: nothing to follow, its Pay is offered)
        if (MARKET_LEGS.some(leg => !current.legs[leg].paid && !current.legs[leg].tx && !sentOf(current.id, leg))) break;
        await sleep(4_000);
        stillOn(run);
        try {
            const answer = await look(current.id);
            stillOn(run);
            current = answer.payment;
            keepPayment(current);
            for (const leg of MARKET_LEGS) {
                const told = answer.told?.[leg];
                if (told?.state === "refused" && sentOf(current.id, leg)?.tx === told.tx) dropSent(current.id, leg);
                const had = followed[leg];
                const has = current.legs[leg].tx?.toLowerCase();
                if (has) followed[leg] = has;
                else if (had) {
                    delete followed[leg];
                    const refused = told?.state === "refused" && told.tx.toLowerCase() === had;
                    if (!current.legs[leg].paid && !refused && sentOf(current.id, leg)?.tx !== had) markMaybeSent(current.id, leg);
                }
            }
            say(payment.listingId, partsWords(current));
        } catch (error) {
            stillOn(run);
            say(payment.listingId, t("Checking your payment: <error>", { error: (error as Error).message }));
        }
    }
    await settled(current, run);
};

const partsWords = (payment: MinePayment): string => {
    if (payment.state === "paid") return t("It's yours! Wear it in Mine.");
    const part = (leg: MarketLegName): "paid" | "on_its_way" | "refused" | "not_sent" => (payment.legs[leg].paid
        ? "paid"
        : payment.legs[leg].tx
            ? "on_its_way"
            : sentOf(payment.id, leg)?.dropped
                ? "refused"
                : sentOf(payment.id, leg) || maybeSent(payment.id, leg) ? "on_its_way" : "not_sent");
    // (each part's words a whole sentence of their own: in, on its way, refused or not sent)
    const burn = part("burn");
    const seller = part("seller");
    return sentences(
        burn === "paid" ? t("Burned part: in.") : burn === "on_its_way" ? t("Burned part: on its way.") : burn === "refused" ? t("Burned part: refused.") : t("Burned part: not sent."),
        seller === "paid" ? t("Seller's part: in.") : seller === "on_its_way" ? t("Seller's part: on its way.") : seller === "refused" ? t("Seller's part: refused.") : t("Seller's part: not sent.")
    );
};

const settled = async(payment: MinePayment, run: Run): Promise<void> => {
    await Promise.all([loadMine(), loadPage()]);
    stillOn(run);
    if (payment.state === "paid" || owns(payment.listingId)) say(payment.listingId, t("It's yours! Wear it in Mine."));
    else if (active(payment)) {
        const missing = MARKET_LEGS.filter(leg => !payment.legs[leg].paid && !payment.legs[leg].tx && !sentOf(payment.id, leg));
        say(payment.listingId, missing.length
            ? sentences(partsWords(payment), t("Press Pay to send <part>.", { part: missing.length > 1 ? t("the burned part and the seller's part") : legWords(missing[0]) }))
            : sentences(partsWords(payment), t("Press “I paid: check” in a minute (don't pay again: it counts once it's in).")), missing.length > 0);
    } else say(payment.listingId, serverWords(payment.note) ?? t("No payment came: nothing was taken."), true);
};

const purchase = async(listingId: string, first: string, work: (run: Run) => Promise<void>): Promise<void> => {
    if (purchaseOn) {
        if (purchaseOn.listingId !== listingId) say(listingId, t("Finish the payment on its way first."), true);
        return;
    }
    const run = startRun();
    const id = ++purchases;
    purchaseOn = { id, listingId };
    following = false;
    try {
        say(listingId, first);
        await work(run);
    } catch (error) {
        if (error instanceof Stopped || !alive(run)) return;
        await loadMine();
        if (alive(run)) say(listingId, (error as Error).message, true);
    } finally {
        if (purchaseOn?.id === id) {
            purchaseOn = undefined;
            following = false;
        }
        redraw();
    }
};

/** a payment to go on with: each part not paid told (this device's transaction), or sent by the wallet; then followed */
const go = async(payment: MinePayment, run: Run): Promise<void> => {
    let current = payment;
    for (const leg of MARKET_LEGS) {
        if (current.state === "paid") break;
        const part = current.legs[leg];
        if (part.paid || part.tx) continue;
        if (sentOf(current.id, leg)) {
            say(current.listingId, t("Telling the flea market <part> you sent first…", { part: legWords(leg) }));
            const told = await tellAgain(current, leg, run);
            if (told) {
                current = told;
                continue;
            }
        }
        if (maybeSent(current.id, leg)) throw new Error(t("Your wallet may have sent <part> already: press “I paid: check” first, and don't pay again yet.", { part: legWords(leg) }));
        // (its payment's time nearly up, the other part on its way: that one in a block first, then this one, any time in
        // the week)
        if (nearlyUp(current, leg) && onItsWay(current, otherLeg(leg))) {
            current = await waitFor(current, otherLeg(leg), run);
            if (current.state === "paid" || !active(current)) break;
            if (nearlyUp(current, leg)) throw new Error(waitWords(otherLeg(leg)));
        }
        current = await payPart(current, leg, run);
    }
    await follow(current, run);
};

/** a part on its way followed until it's in a block (looked for every 4 s, two minutes at most) */
const waitFor = async(payment: MinePayment, leg: MarketLegName, run: Run): Promise<MinePayment> => {
    following = true;
    say(payment.listingId, leg === "burn"
        ? t("The burned part is on its way: waiting for it to go in before the rest is sent…")
        : t("The seller's part is on its way: waiting for it to go in before the rest is sent…"));
    const deadline = Date.now() + 2 * 60_000;
    let current = payment;
    while (!current.legs[leg].paid && current.state === "waiting" && Date.now() < deadline) {
        await sleep(4_000);
        stillOn(run);
        try {
            const answer = await look(current.id);
            stillOn(run);
            current = answer.payment;
            keepPayment(current);
        } catch {
            stillOn(run);
        }
    }
    following = false;
    return current;
};

/** a look asked again while the market couldn't look whole (its 3 s spacing, the node silent), three times at most */
const lookedUp = async(run: Run, request: () => Promise<Answer>): Promise<Answer> => {
    for (let tries = 1; ; tries++) {
        const answer = await request();
        stillOn(run);
        if (answer.looked !== false || answer.payment.state === "paid" || tries >= 3) return answer;
    }
};

const buy = (listingId: string): Promise<void> => purchase(listingId, t("Asking the flea market…"), async run => {
    // (a part this device sent for a payment of it given up since: told once more first. It may count all the same, late:
    // that payment is then half paid, its rest to send. A new payment, and the wallet asked again, only once the market
    // refuses it; 0.98 review: the store's lateSentFor)
    const late = lateSentFor(listingId);
    if (late) {
        say(listingId, t("Looking at your earlier payment first…"));
        const answer = await lookedUp(run, () => tell(late.payment.id, late.leg, late.tx, true));
        keepPayment(answer.payment);
        const told = answer.told?.[late.leg];
        if (answer.payment.state === "paid" || told?.state === "owned") return settled(answer.payment, run);
        if (active(answer.payment)) return go(answer.payment, run);
        if (answer.looked === false || told?.state !== "refused") throw new Error(onItsWayWords());
        forgetSent(late.payment.id, late.leg);
    }
    // (at the price the card showed: a seller's change meanwhile is said, the card shown again at its price; 0.98 review)
    const seen = shown.find(item => item.id === listingId)?.priceAtomic ?? mine.paying?.find(item => item.id === listingId)?.priceAtomic;
    const { payment } = await api<{ payment: MinePayment }>("/market/buy", { method: "POST", body: JSON.stringify({ listingId, ...(seen ? { priceAtomic: seen } : {}) }) }).catch(async(error: unknown) => {
        await loadPage();
        throw error;
    });
    stillOn(run);
    keepPayment(payment);
    await go(payment, run);
});

/** a payment to finish (a part cancelled in the wallet, refused, or left): looked for first, then paid */
const payAgain = (paymentId: string, listingId: string): Promise<void> => purchase(listingId, t("Looking for it first…"), async run => {
    if (checking.has(paymentId)) throw new Error(t("It's being looked for: wait for the answer first."));
    const answer = await look(paymentId, true);
    stillOn(run);
    const payment = answer.payment;
    keepPayment(payment);
    if (payment.state === "paid" || !active(payment)) return settled(payment, run);
    if (answer.looked === false) throw new Error(couldNotLookWords());
    await go(payment, run);
});

/** a payment looked for at the player's word: this device's transactions told, else the coin's records searched */
const check = async(paymentId: string, listingId: string): Promise<void> => {
    if (checking.has(paymentId)) return;
    const run = startRun();
    checking.add(paymentId);
    try {
        say(listingId, t("Looking for your payment…"));
        const known = mine.payments.find(payment => payment.id === paymentId);
        let answer: Answer | undefined;
        for (const leg of MARKET_LEGS) {
            const sent = sentOf(paymentId, leg);
            if (!known || known.legs[leg].paid || known.legs[leg].tx || !sent) continue;
            answer = await tell(paymentId, leg, sent.tx, true);
            stillOn(run);
            const refused = afterTold(answer, leg, sent.tx);
            if (refused && sent.dropped && !lateAlone(answer.payment, leg)) forgetSent(paymentId, leg);
        }
        answer = answer && answer.looked !== false ? answer : await look(paymentId, true);
        stillOn(run);
        keepPayment(answer.payment);
        checking.delete(paymentId);
        await Promise.all([loadMine(), loadPage()]);
        stillOn(run);
        const payment = answer.payment;
        if (payment.state === "paid" || owns(listingId)) say(listingId, t("It's yours! Wear it in Mine."));
        else if (answer.looked === false) say(listingId, couldNotLookWords(), true);
        else say(listingId, payment.note ? sentences(sentence(tr(payment.note)), partsWords(payment)) : partsWords(payment), !active(payment));
    } catch (error) {
        checking.delete(paymentId);
        if (!(error instanceof Stopped) && alive(run)) say(listingId, (error as Error).message, true);
    } finally {
        checking.delete(paymentId);
        redraw();
    }
};

// ---- the views
const open = (): boolean => page?.open === true && !page.problem;

const card = (item: MarketListingView): string => {
    const me = storeBuyer()?.address?.toLowerCase();
    const ownListing = me !== undefined && item.seller === me;
    const owned = owns(item.id);
    const payment = owned || ownListing ? undefined : activeFor(item.id);
    const late = owned || ownListing || payment ? undefined : lateFor(item.id);
    const note = said.get(item.id);
    const busy = (words: string): string => `<button type="button" class="store-buy" disabled>${words}</button>`;
    const checkButton = (words: string, kind = "store-buy"): string => payment ? `<button type="button" class="${kind}" data-market-check="${escapeHtml(payment.id)}" data-market-item="${escapeHtml(item.id)}">${words}</button>` : "";
    let action: string;
    let extra = "";
    if (owned) action = `<span class="store-owned"><i class="fa-solid fa-check"></i> ${t("Yours")}</span>`;
    else if (ownListing) action = `<span class="store-owned">${t("Your listing")}</span>`;
    else if (purchaseOn?.listingId === item.id) action = busy(following ? t("Checking…") : t("Paying…"));
    else if (payment && checking.has(payment.id)) action = busy(t("Looking…"));
    else if (payment) {
        // (Pay only for a part nothing this device knows of may pay: none sent, maybe sent or held for the other in its
        // last five minutes; 0.98 review: Pay the rest was offered beside a part maybe sent, then refused)
        const missing = MARKET_LEGS.filter(leg => !payment.legs[leg].paid && !payment.legs[leg].tx && !sentOf(payment.id, leg));
        const held = MARKET_LEGS.some(leg => !payment.legs[leg].paid && maybeSent(payment.id, leg)) || missing.some(leg => nearlyUp(payment, leg));
        if (payingHere.has(payment.id)) action = busy(t("Paying…"));
        else if (payingElsewhere(payment.id)) action = busy(t("Paying in another tab…"));
        else if (missing.length && payment.pay && !held) {
            action = `<button type="button" class="store-buy" data-market-pay="${escapeHtml(payment.id)}" data-market-item="${escapeHtml(item.id)}">${missing.length === 2 ? t("Pay") : t("Pay the rest")}</button>`;
            extra = checkButton(t("Paid it already? Check"), "store-late");
        } else action = checkButton(t("I paid: check"));
    } else if (!item.onSale) action = busy(t("Not on sale"));
    else if (item.left === 0) action = busy(t("Sold out"));
    else if (!open()) action = busy(t("Market closed"));
    else if (mine.walletRequired) action = `<button type="button" class="store-buy" disabled title="${escapeHtml(t("Sign in with your wallet to buy"))}">${t("Sign in to buy")}</button>`;
    else action = `<button type="button" class="store-buy" data-market-buy="${escapeHtml(item.id)}">${t("Buy")}</button>`;
    if (late && !purchaseOn) extra = `<button type="button" class="store-late" data-market-check="${escapeHtml(late.id)}" data-market-item="${escapeHtml(item.id)}">${checking.has(late.id) ? t("Looking…") : t("Paid it already? Look again")}</button>`;
    const price = payment ? payment.priceAtomic : item.priceAtomic;
    const seller = item.sellerName ? escapeHtml(item.sellerName) : escapeHtml(short(item.seller));
    return `<div class="store-card market-card${owned ? " owned" : ""}" data-card="${escapeHtml(item.id)}" tabindex="-1">
        <img class="store-art" src="${escapeHtml(item.url)}" alt="" loading="lazy" decoding="async">
        <div class="store-name">${escapeHtml(item.name)}</div>
        <div class="market-seller">${t("by <seller>", { seller })}</div>
        <div class="store-price">${owned ? "" : escapeHtml(amount(price))}${!owned && item.left !== undefined && item.onSale ? ` <span class="store-left">· ${Number(item.left) === 1 ? t("1 left") : t("<n> left", { n: Number(item.left) })}</span>` : ""}</div>
        <div class="market-sold">${Number(item.sold) === 1 ? t("1 sold") : t("<n> sold", { n: Number(item.sold) })}</div>
        ${action}
        ${extra}
        ${note ? `<p class="store-said${note.bad ? " bad" : ""}">${escapeHtml(note.text)}</p>` : payment ? `<p class="store-said">${escapeHtml(payment.note ? sentences(sentence(tr(payment.note)), partsWords(payment)) : partsWords(payment))}</p>` : ""}
    </div>`;
};

const subTabs = (): string => {
    const button = (id: View, words: string) => `<button type="button" class="market-view${view === id ? " on" : ""}" data-market-view="${id}" aria-pressed="${view === id}">${words}</button>`;
    return `<div class="market-views">${button("browse", t("Browse"))}${button("sell", t("Sell a design"))}${button("listings", t("My listings"))}${view === "browse" ? `<label class="market-sort">${t("Show")} <select data-market-sort><option value="new"${sort === "new" ? " selected" : ""}>${t("Newest")}</option><option value="popular"${sort === "popular" ? " selected" : ""}>${t("Most bought")}</option></select></label>` : ""}</div>`;
};

const browseView = (): string => {
    if (!page) return failed ? `<p class="store-empty">${t("The flea market couldn't be loaded just now:")} <button type="button" class="store-buy" data-market-retry="1">${t("Try again")}</button></p>` : `<p class="store-empty">${t("Loading the flea market…")}</p>`;
    // (a listing with a payment of the wallet's on its way stays shown, taken off sale or not)
    const extraIds = mine.payments.filter(active).map(payment => payment.listingId).filter(id => !shown.some(item => item.id === id));
    const extra = extraIds.flatMap(id => mine.paying?.find(item => item.id === id) ?? mine.listings.find(item => item.id === id) ?? mine.bought.find(item => item.id === id) ?? []);
    const items = [...shown, ...extra];
    if (!items.length) return `<p class="store-empty">${t("Nothing on the flea market yet: draw an emblem, save it in Customize, and sell it here.")}</p>`;
    return `<div class="store-grid">${items.map(card).join("")}</div>${shown.length < page.total ? `<p class="market-more"><button type="button" class="store-buy" data-market-more="1">${t("More")}</button></p>` : ""}`;
};

const sellView = (): string => {
    if (mine.walletRequired) return `<p class="store-empty">${t("Sign in with your wallet to sell your designs: what they sell for goes straight to your wallet. Guests can't sell.")}</p>`;
    if (mine.banned) return `<p class="store-empty">${t("Your wallet can't sell on the flea market while it's banned.")}</p>`;
    if (!open()) return `<p class="store-empty">${t("The flea market is closed right now: you can sell once it opens.")}</p>`;
    const designs = savedDesigns().filter(design => design.ops?.length);
    if (!designs.length) return `<p class="store-empty">${t("Draw an emblem in Customize and save it first: your saved designs show here, to sell.")}</p>`;
    // (0.99: one on the flea market already, or drawn over a skin, isn't for sale: only an original, drawn on the uniform)
    const why = (design: (typeof designs)[number]): string | undefined => (design.onMarket ? t("on the flea market") : design.drawnOver ? t("drawn over a skin: not for sale") : undefined);
    const picked = designs.find(design => design.name === sellForm.design);
    if (!picked || why(picked)) sellForm.design = "";
    const symbol = page?.token?.symbol ?? t("tokens");
    const rows = designs.map((design, index) => {
        const not = why(design);
        return `<label class="store-wear market-design${not ? " unsellable" : ""}"><input type="radio" name="market-design" value="${index}" ${sellForm.design !== "" && sellForm.design === design.name ? "checked" : ""}${not ? " disabled" : ""}><canvas width="72" height="72" data-market-thumb="${index}" aria-hidden="true"></canvas><span>${escapeHtml(design.name)}${not ? `<small>${escapeHtml(not)}</small>` : ""}</span></label>`;
    }).join("");
    return `<h4>${t("Pick one of your designs")}</h4>
        <div class="store-wear-list">${rows}</div>
        <div class="market-form">
            <label><span>${t("Name it")}</span><input type="text" maxlength="${MARKET_LIMITS.nameMax}" data-market-field="name" value="${escapeHtml(sellForm.name)}" placeholder="${escapeHtml(t("What buyers see"))}"></label>
            <label><span>${t("Price (<coin>)", { coin: escapeHtml(symbol) })}</span><input type="text" inputmode="decimal" data-market-field="price" value="${escapeHtml(sellForm.price)}" placeholder="10"></label>
            <label><span>${t("Copies")} <small>${t("(empty: no limit)")}</small></span><input type="text" inputmode="numeric" data-market-field="stock" value="${escapeHtml(sellForm.stock)}" placeholder="${escapeHtml(t("No limit"))}"></label>
            <button type="button" class="store-buy" data-market-sell="1" ${selling ? "disabled" : ""}>${selling ? t("Checking your design…") : t("Put it up for sale")}</button>
        </div>
        <p class="market-rules">${t("A tenth of each sale is burned; the rest goes straight from the buyer's wallet to yours. Buyers wear it as a picture: they can't edit it or sell it again. Your design has to be your own, drawn from scratch on the uniform: one drawn over a skin, or over a store emblem or another player's listing, is refused. While it's on sale it leaves your designs (you can't wear it); take it off the market to have it back.")}</p>
        ${sellSaid ? `<p class="store-said${sellSaid.bad ? " bad" : ""}">${escapeHtml(sellSaid.text)}</p>` : ""}`;
};

const stateWords = (): Readonly<Record<ListingState, string>> => ({ on_sale: t("On sale"), withdrawn: t("Off the market"), removed: t("Taken down") });
const listingsView = (): string => {
    if (mine.walletRequired) return `<p class="store-empty">${t("Sign in with your wallet to see your listings.")}</p>`;
    const symbol = page?.token?.symbol ?? t("tokens");
    const rows = mine.listings.map(item => {
        const edit = edits.get(item.id) ?? {};
        const busy = listingBusy.has(item.id);
        const note = listingSaid.get(item.id);
        const removed = item.state === "removed";
        return `<div class="market-listing" data-card="${escapeHtml(item.id)}" tabindex="-1">
            <img class="store-art" src="${escapeHtml(item.url)}" alt="" loading="lazy" decoding="async">
            <div class="market-listing-body">
                <div class="store-name">${escapeHtml(item.name)} <span class="market-state ${escapeHtml(item.state)}">${escapeHtml(stateWords()[item.state] ?? item.state)}</span></div>
                <div class="market-sold">${escapeHtml(amount(item.priceAtomic))} · ${Number(item.sold) === 1 ? t("1 sold") : t("<n> sold", { n: Number(item.sold) })}${item.stock !== undefined ? ` · ${Number(item.stock) === 1 ? t("1 copy left") : t("<n> copies left", { n: Number(item.stock) })}` : ""}${item.repriceNeeded ? ` · ${t("needs a price in the game's token now")}` : ""}${item.fromDesign ? ` · ${item.state === "on_sale" ? t("out of your designs: “<design>”", { design: escapeHtml(item.fromDesign) }) : t("back in your designs: “<design>”", { design: escapeHtml(item.fromDesign) })}` : ""}</div>
                ${removed ? `<p class="store-said bad">${item.removedNote ? t("The owner took it down: <note>. Its buyers can't wear it meanwhile.", { note: escapeHtml(item.removedNote) }) : t("The owner took it down. Its buyers can't wear it meanwhile.")}</p>` : `<div class="market-form small">
                    <label><span>${t("Price (<coin>)", { coin: escapeHtml(symbol) })}</span><input type="text" inputmode="decimal" data-market-edit="price" data-market-listing="${escapeHtml(item.id)}" value="${escapeHtml(edit.price ?? formatTokenAmount(item.priceAtomic, page?.token?.decimals ?? 18))}"></label>
                    <label><span>${t("Copies")}</span><input type="text" inputmode="numeric" data-market-edit="stock" data-market-listing="${escapeHtml(item.id)}" value="${escapeHtml(edit.stock ?? (item.stock === undefined ? "" : String(item.stock)))}" placeholder="${escapeHtml(t("No limit"))}"></label>
                    <button type="button" class="store-buy" data-market-save="${escapeHtml(item.id)}" ${busy ? "disabled" : ""}>${t("Save")}</button>
                    <button type="button" class="store-buy secondary" data-market-toggle="${escapeHtml(item.id)}" ${busy ? "disabled" : ""}>${item.state === "on_sale" ? t("Take off the market") : t("Put back on sale")}</button>
                    ${item.sold === 0 ? `<button type="button" class="store-late" data-market-delete="${escapeHtml(item.id)}" ${busy ? "disabled" : ""}>${t("Delete")}</button>` : ""}
                </div>`}
                ${note ? `<p class="store-said${note.bad ? " bad" : ""}">${escapeHtml(note.text)}</p>` : ""}
            </div>
        </div>`;
    }).join("");
    const bought = mine.bought.length
        ? `<h4>${t("Bought")}</h4><div class="store-wear-list">${mine.bought.map(item => `<span class="store-wear"><img src="${escapeHtml(item.url)}" alt=""><span>${item.wearable ? escapeHtml(item.name) : t("<name> (taken down)", { name: escapeHtml(item.name) })}</span></span>`).join("")}</div><p class="store-said">${t("Wear them in Mine.")}</p>`
        : "";
    return `${rows || `<p class="store-empty">${t("Nothing for sale yet: sell one of your designs.")}</p>`}${bought}`;
};

/** a listing change at the seller's word: its price and copies, on sale or not, or deleted */
const changeListing = async(listingId: string, change: Record<string, unknown>, done: string): Promise<void> => {
    if (listingBusy.has(listingId)) return;
    const run = startRun();
    listingBusy.add(listingId);
    listingSaid.delete(listingId);
    try {
        redraw();
        await api(`/market/listings/${encodeURIComponent(listingId)}`, { method: "POST", body: JSON.stringify(change) });
        stillOn(run);
        edits.delete(listingId);
        // (its design out of the loadout, or back in it: Customize's designs read again too, 0.99)
        await Promise.all([loadMine(), loadPage(), refreshSavedDesigns()]);
        stillOn(run);
        listingSaid.set(listingId, { text: done });
        announce(done);
    } catch (error) {
        if (!(error instanceof Stopped) && alive(run)) listingSaid.set(listingId, { text: (error as Error).message, bad: true });
    } finally {
        listingBusy.delete(listingId);
        redraw();
    }
};

const sell = async(): Promise<void> => {
    if (selling) return;
    const designs = savedDesigns().filter(design => design.ops?.length);
    const design = sellForm.design === "" ? undefined : designs.find(each => each.name === sellForm.design);
    if (!design?.ops || design.onMarket || design.drawnOver) {
        sellSaid = { text: design?.drawnOver ? t("That one was drawn over a skin: only a design drawn from scratch on the uniform can be sold.") : design?.onMarket ? t("That one is on the flea market already.") : t("Pick one of your designs first."), bad: true };
        redraw();
        return;
    }
    const run = startRun();
    selling = true;
    sellSaid = { text: t("Drawing your design and checking it against the store's emblems and the other listings…") };
    try {
        redraw();
        const { listing } = await api<{ listing: MarketListingView }>("/market/listings", {
            method: "POST",
            // (the saved design by its name: the server takes it from your designs, as it keeps them; 0.99)
            body: JSON.stringify({ design: design.name, name: sellForm.name || design.name, price: sellForm.price, stock: sellForm.stock.trim() === "" ? null : sellForm.stock })
        });
        stillOn(run);
        resetSellForm();
        sellSaid = { text: t("“<name>” is on the flea market at <price>. It's out of your designs while it's on sale: take it off the market to wear it again.", { price: amount(listing.priceAtomic), name: listing.name }) };
        announce(sellSaid.text);
        await Promise.all([loadMine(), loadPage(), refreshSavedDesigns()]);
    } catch (error) {
        if (!(error instanceof Stopped) && alive(run)) {
            sellSaid = { text: (error as Error).message, bad: true };
            announce(sellSaid.text);
        }
    } finally {
        selling = false;
        redraw();
    }
};

export const MarketMenu = {
    /** how the store's list is drawn again, and how a screen reader is told (storeMenu.ts) */
    attach(options: { readonly redraw: () => void, readonly announce: (text: string) => void }): void {
        redraw = options.redraw;
        announce = options.announce;
    },

    async refresh(): Promise<void> {
        const now = storeBuyer()?.address;
        if (now !== lastBuyer) {
            lastBuyer = now;
            generation++;
            purchaseOn = undefined;
            following = false;
            checking.clear();
            selling = false;
            sellSaid = undefined;
            resetSellForm();
        }
        await Promise.all([loadPage(), loadMine()]);
    },

    /** the store's line over the list, for the flea market */
    note(): string {
        const symbol = page?.token?.symbol ?? t("the game's token");
        if (!page) return t("Players' own emblems, for sale to each other.");
        if (page.problem) return tr(page.problem);
        if (!page.open) return t("The flea market is closed right now.");
        return page.burnMethod === "transfer"
            ? t("Players' own emblems, paid in <coin> from your wallet in two parts: a tenth is burned (sent to the dead address), the rest goes straight to the seller. Bought emblems are worn as pictures.", { coin: symbol })
            : t("Players' own emblems, paid in <coin> from your wallet in two parts: a tenth is burned, the rest goes straight to the seller. Bought emblems are worn as pictures.", { coin: symbol });
    },

    html(): string {
        return `${subTabs()}${view === "browse" ? browseView() : view === "sell" ? sellView() : listingsView()}`;
    },

    /** the design thumbnails drawn (after the list is) */
    afterDraw(list: HTMLElement): void {
        const designs = savedDesigns().filter(design => design.ops?.length);
        list.querySelectorAll<HTMLCanvasElement>("canvas[data-market-thumb]").forEach(canvas => {
            const ops = designs[Number(canvas.dataset.marketThumb)]?.ops as Emblem | undefined;
            const ctx = canvas.getContext("2d");
            if (!ctx || !ops) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();
            ctx.scale(canvas.width / 256, canvas.height / 256);
            drawEmblem(ctx, ops, 256);
            ctx.restore();
        });
    },

    /** whether a field of the flea market's is being typed in (the list isn't drawn again under the player's typing) */
    typing(list: HTMLElement): boolean {
        const active = document.activeElement;
        return active instanceof HTMLInputElement && list.contains(active) && (active.dataset.marketField !== undefined || active.dataset.marketEdit !== undefined);
    },

    /** a click in the list: whether it was the flea market's */
    click(target: HTMLElement): boolean {
        const button = target.closest<HTMLElement>("[data-market-view], [data-market-buy], [data-market-pay], [data-market-check], [data-market-more], [data-market-retry], [data-market-sell], [data-market-save], [data-market-toggle], [data-market-delete]");
        if (!button) return false;
        const data = button.dataset;
        if (data.marketView) {
            view = data.marketView as View;
            if (view === "sell") void refreshSavedDesigns().then(() => redraw());
            redraw();
        } else if (data.marketBuy) void buy(data.marketBuy);
        else if (data.marketPay && data.marketItem) void payAgain(data.marketPay, data.marketItem);
        else if (data.marketCheck && data.marketItem) void check(data.marketCheck, data.marketItem);
        else if (data.marketRetry) void loadPage().then(redraw);
        else if (data.marketMore && page && !moreLoading) {
            moreLoading = true;
            const sorted = sort;
            void api<MarketPublicView>(`/market?sort=${sorted}&offset=${shown.length}`).then(next => {
                if (sorted !== sort || !page) return;
                page = { ...page, total: next.total };
                shown = once([...shown, ...next.listings]);
            }).catch(() => undefined).finally(() => {
                moreLoading = false;
                redraw();
            });
        } else if (data.marketSell) void sell();
        else if (data.marketSave) {
            const edit = edits.get(data.marketSave) ?? {};
            const listing = mine.listings.find(item => item.id === data.marketSave);
            void changeListing(data.marketSave, {
                ...(edit.price !== undefined ? { price: edit.price } : {}),
                ...(edit.stock !== undefined ? { stock: edit.stock.trim() === "" ? null : edit.stock } : {}),
                ...(edit.price === undefined && edit.stock === undefined && listing ? { name: listing.name } : {})
            }, t("Saved."));
        } else if (data.marketToggle) {
            const listing = mine.listings.find(item => item.id === data.marketToggle);
            if (listing) void changeListing(listing.id, { onSale: listing.state !== "on_sale" }, listing.state === "on_sale" ? t("Taken off the market: it's back in your designs, to wear (Customize). Its buyers keep it.") : t("Back on sale: it's out of your designs while it is."));
        } else if (data.marketDelete) {
            const listing = mine.listings.find(item => item.id === data.marketDelete);
            void changeListing(data.marketDelete, { delete: true }, listing?.fromDesign ? t("Deleted: “<design>” is back in your designs. Its buyers keep it.", { design: listing.fromDesign }) : t("Deleted."));
        }
        return true;
    },

    /** a field changed (typed, picked): whether it was the flea market's */
    change(target: HTMLInputElement | HTMLSelectElement): boolean {
        if (target instanceof HTMLSelectElement && target.dataset.marketSort !== undefined) {
            sort = target.value === "popular" ? "popular" : "new";
            shown = [];
            void loadPage().then(redraw);
            return true;
        }
        if (!(target instanceof HTMLInputElement)) return false;
        if (target.name === "market-design") {
            const design = savedDesigns().filter(each => each.ops?.length)[Number(target.value)];
            sellForm.design = design && !design.onMarket && !design.drawnOver ? design.name : "";
            if (design && sellForm.design && !sellForm.name) sellForm.name = design.name.slice(0, MARKET_LIMITS.nameMax);
            redraw();
            return true;
        }
        const field = target.dataset.marketField as keyof typeof sellForm | undefined;
        if (field && field !== "design") {
            sellForm[field] = target.value;
            return true;
        }
        const edit = target.dataset.marketEdit as "price" | "stock" | undefined;
        const listing = target.dataset.marketListing;
        if (edit && listing) {
            edits.set(listing, { ...(edits.get(listing) ?? {}), [edit]: target.value });
            return true;
        }
        return false;
    },

    /** the emblems the wallet bought, to wear (the Mine tab) */
    bought(): ReadonlyArray<{ readonly id: string, readonly name: string, readonly url: string, readonly wearable: boolean }> {
        return mineFor !== undefined && mineFor === storeBuyer()?.address ? mine.bought : [];
    },

    /** a bought emblem's name and picture, by its id */
    emblem(id: string): { readonly name: string, readonly url: string } | undefined {
        return mine.bought.find(item => item.id === id);
    },

    /** a payment of the flea market's on its way (the store's list is drawn again every few seconds meanwhile) */
    get busy(): boolean {
        return purchaseOn !== undefined || checking.size > 0 || selling;
    },

    /** the keys of what this device remembers of its payments (another tab's change draws the cards again) */
    storageKeys: Object.freeze([SENT_KEY, MAYBE_KEY, PAYING_KEY]) as readonly string[],

    /**
     * when the first hold a card shows ends, for the cards to be drawn again then (nothing else may draw them): another
     * tab's paying mark, a part maybe sent, Pay's last five minutes, a payment's end, a half-paid one's week
     */
    holdsEnd(): number | undefined {
        const now = Date.now();
        const paying = readRecord<number>(PAYING_KEY);
        const maybe = readRecord<number>(MAYBE_KEY);
        let next = Number.POSITIVE_INFINITY;
        for (const payment of mine.payments) {
            if (!active(payment)) continue;
            const ends = [Number(paying[payment.id]) + PAYING_ELSEWHERE_MS, payment.expiresAt - clockSkew - PAY_MARGIN_MS, payment.expiresAt - clockSkew, payment.expiresAt - clockSkew + MARKET_LIMITS.latePaymentMs];
            for (const leg of MARKET_LEGS) ends.push(Number(maybe[partKey(payment.id, leg)] ?? maybeHere.get(partKey(payment.id, leg))) + MAYBE_SENT_MS);
            for (const at of ends) if (Number.isFinite(at) && at > now) next = Math.min(next, at);
        }
        return next < Number.POSITIVE_INFINITY ? next : undefined;
    }
};

/** (each part's words, for the tests' look at what this module says: a part's name in the player's language) */
export const MARKET_PART_WORDS = legWords;
